'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import type { YouTubeVideo } from '@/lib/youtube';

/**
 * YouTube動画カルーセル（クライアント）
 * 目的: YouTube動画をカード形式でスクロール表示する
 * 仕様:
 *   - モバイル: 1枚表示、左右に次のカードがちら見え
 *   - タブレット: 2枚表示
 *   - デスクトップ: 3枚表示
 *   - 左右矢印で手動スクロール
 *   - クリックでYouTube埋め込み再生（モーダル）
 */
export default function YouTubeCarouselClient({
  videos,
}: {
  videos: YouTubeVideo[];
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector<HTMLElement>('[data-video-card]')?.clientWidth ?? 300;
    const gap = 24;
    const amount = (cardWidth + gap) * (direction === 'left' ? -1 : 1);
    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  if (videos.length === 0) return null;

  return (
    <>
      <div className='relative'>
        {/* スクロールコンテナ */}
        <div
          ref={scrollRef}
          className='flex gap-6 overflow-x-auto scroll-smooth pb-2 snap-x snap-mandatory scrollbar-hide'
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {videos.map((v) => (
            <button
              key={v.id}
              type='button'
              data-video-card
              onClick={() => setPlayingId(v.id)}
              className='group shrink-0 w-[85%] sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] snap-start text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2'
            >
              <div className='overflow-hidden rounded-none border border-gray-200 bg-white shadow-sm transition-shadow duration-300 group-hover:shadow-lg'>
                {/* サムネイル */}
                <div className='relative aspect-video bg-gray-100'>
                  <Image
                    src={v.thumbnailUrl}
                    alt={v.title}
                    fill
                    className='object-cover'
                    sizes='(min-width: 1024px) 400px, (min-width: 640px) 50vw, 85vw'
                  />
                  {/* 再生ボタンオーバーレイ */}
                  <div className='absolute inset-0 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/20'>
                    <div className='flex h-14 w-14 items-center justify-center rounded-full bg-red-600 shadow-lg transition-transform group-hover:scale-110'>
                      <svg
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='white'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path d='M8 5v14l11-7z' />
                      </svg>
                    </div>
                  </div>
                </div>
                {/* タイトル */}
                <div className='p-4'>
                  <h3 className='line-clamp-2 text-sm font-bold leading-snug text-gray-800 group-hover:text-primary md:text-base'>
                    {v.title}
                  </h3>
                  <p className='mt-1 text-xs text-gray-400'>
                    {formatDate(v.publishedAt)}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* 左矢印 */}
        {canScrollLeft && (
          <button
            type='button'
            onClick={() => scroll('left')}
            className='absolute -left-3 top-[35%] z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-md transition-colors hover:bg-white md:flex'
            aria-label='前へ'
          >
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
              <path
                d='M15 18l-6-6 6-6'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        )}

        {/* 右矢印 */}
        {canScrollRight && (
          <button
            type='button'
            onClick={() => scroll('right')}
            className='absolute -right-3 top-[35%] z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-md transition-colors hover:bg-white md:flex'
            aria-label='次へ'
          >
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
              <path
                d='M9 18l6-6-6-6'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        )}
      </div>

      {/* 動画再生モーダル */}
      {playingId && (
        <div
          className='fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4'
          onClick={() => setPlayingId(null)}
        >
          <div
            className='relative w-full max-w-4xl'
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type='button'
              onClick={() => setPlayingId(null)}
              className='absolute -top-12 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/40'
              aria-label='閉じる'
            >
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M6 6l12 12M18 6l-12 12'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </svg>
            </button>
            <div className='aspect-video overflow-hidden rounded-xl bg-black'>
              <iframe
                src={`https://www.youtube.com/embed/${playingId}?autoplay=1`}
                title='YouTube video player'
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                allowFullScreen
                className='h-full w-full'
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}
